import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useEvents } from '../hooks/useEvents';
import { useEmailSync } from '../hooks/useEmailSync';
import { apiCall } from '../utils/api';
import TasksTab from '../components/TasksTab';
import FilesTab from '../components/FilesTab';
import CalendarTab from '../components/CalendarTab';
import CommunityTab from '../components/CommunityTab';
import AuditTab from '../components/AuditTab';
import AdminTab from '../components/AdminTab';
import ClubDevelopmentPlanTab from '../components/ClubDevelopmentPlanTab';
import IdeasTab from '../components/IdeasTab';
import OverdueSection from '../components/OverdueSection';
import UpcomingSection from '../components/UpcomingSection';
import StatsCards from '../components/StatsCards';
import CommunityMessagesBar from '../components/CommunityMessagesBar';
import EmailSyncStatus from '../components/EmailSyncStatus';
import UserGuide from '../components/UserGuide';

export default function Dashboard({ email, token }) {
  const { logout } = useAuth();
  const { events, updateEvent } = useEvents();
  const { syncStatus, newEmails, isSyncing, error, syncEmails } = useEmailSync();
  const [files, setFiles] = useState([]);
  const [filesLoading, setFilesLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [isAdmin, setIsAdmin] = useState(false);
  const [showGuide, setShowGuide] = useState(false);

  const loadFiles = useCallback(async () => {
    try {
      setFilesLoading(true);
      const data = await apiCall('/api/files');
      setFiles(data);
    } catch (err) {
      console.error('Error loading files:', err);
    } finally {
      setFilesLoading(false);
    }
  }, []);

  // Check if user is admin
  useEffect(() => {
    const checkAdmin = async () => {
      try {
        const response = await apiCall('/api/auth/me');
        setIsAdmin(response.isAdmin || false);
      } catch (err) {
        console.error('Error checking admin status:', err);
        setIsAdmin(false);
      }
    };
    checkAdmin();
  }, []);

  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => {
    // Delay file loading to reduce concurrent API calls
    const timer = setTimeout(() => {
      loadFiles();
    }, 500);
    return () => clearTimeout(timer);
  }, [loadFiles]);

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getTypeLabel = (type) => {
    const labels = {
      task: 'Task',
      milestone: 'Milestone',
      decision: 'Decision',
      meeting: 'Meeting',
    };
    return labels[type] || type;
  };

  const getTypeColor = (type) => {
    const colors = {
      task: 'bg-primary-fixed text-on-primary-fixed',
      milestone: 'bg-secondary-fixed text-on-secondary-fixed',
      decision: 'bg-tertiary-fixed text-on-tertiary-fixed',
      meeting: 'badge-primary',
    };
    return colors[type] || 'badge-primary';
  };

  // Calculate overdue and upcoming
  const today = new Date().toISOString().split('T')[0];
  const in30Days = new Date();
  in30Days.setDate(in30Days.getDate() + 30);
  const in30DaysStr = in30Days.toISOString().split('T')[0];

  const overdue = events
    .filter((e) => e.date < today && e.status !== 'completed' && e.status !== 'cancelled')
    .sort((a, b) => a.date.localeCompare(b.date));

  const upcoming = events
    .filter(
      (e) =>
        e.date >= today &&
        e.date <= in30DaysStr &&
        e.status !== 'completed' &&
        e.status !== 'cancelled'
    )
    .sort((a, b) => a.date.localeCompare(b.date));

  return (
    <div className="min-h-screen bg-surface text-on-surface">
      {/* Header and Navigation Combined */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-surface-container-lowest shadow-sm">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-2 sm:py-3 space-y-2 sm:space-y-0">
          {/* Top row: Title and buttons */}
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <h1 className="text-lg sm:text-2xl font-bold font-headline text-primary leading-tight">EGAC Strategy Hub</h1>
              <p className="text-xs sm:text-sm text-on-surface-variant">
                Track readiness & club strategy
              </p>
            </div>
            <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
              <a
                href="/guide/"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-lg bg-surface-container hover:bg-surface-container-high transition-colors text-sm"
                title="User guide — how to use the site"
              >
                📖
              </a>
              <a
                href="/architecture/"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-lg bg-surface-container hover:bg-surface-container-high transition-colors text-sm"
                title="Architecture — how this site is built"
              >
                🗺️
              </a>
              {isAdmin && (
                <button
                  onClick={syncEmails}
                  disabled={isSyncing}
                  className="p-2 rounded-lg bg-surface-container hover:bg-surface-container-high disabled:opacity-50 transition-colors text-sm"
                  title="Sync WhatsApp chats and emails (admin only)"
                >
                  {isSyncing ? '⟳' : '🔄'}
                </button>
              )}
              <button onClick={logout} className="btn-primary text-xs px-2 sm:px-3 py-1 sm:py-2">
                <span className="hidden sm:inline">Log out</span>
                <span className="sm:hidden">Exit</span>
              </button>
            </div>
          </div>
          {/* Bottom row: Email on mobile only */}
          <div className="hidden max-sm:block">
            <p className="text-xs text-on-surface-variant truncate">{email}</p>
          </div>
          {/* Email on desktop */}
          <div className="hidden sm:flex items-center justify-end">
            <span className="text-xs text-on-surface-variant">{email}</span>
          </div>
        </div>

        {/* Navigation Tabs - Part of Header */}
        <nav className="border-t border-outline-variant overflow-x-auto">
          <div className="max-w-7xl mx-auto px-2 sm:px-6 flex gap-0 sm:gap-2 min-w-max sm:min-w-0">
            {[
              { value: 'dashboard', label: 'Dashboard' },
              { value: 'tasks', label: 'Tasks' },
              { value: 'files', label: 'Files' },
              { value: 'calendar', label: 'Calendar' },
              { value: 'ideas', label: '💡 Ideas' },
              { value: 'club-dev-plan', label: '📋 Plan' },
              { value: 'community', label: '💬 Community' },
              { value: 'audit', label: '📋 Audit' },
              { value: 'admin', label: '⚙️ Admin' },
            ].map((tab) => (
              <button
                key={tab.value}
                className={`px-2 sm:px-4 py-3 text-xs sm:text-sm font-semibold border-b-2 whitespace-nowrap transition-colors ${
                  activeTab === tab.value
                    ? 'border-b-2 border-blue-600 text-blue-600 font-bold'
                    : 'border-b-2 border-transparent text-slate-800 hover:text-blue-600 hover:font-bold'
                }`}
                onClick={() => setActiveTab(tab.value)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </nav>
      </header>

      {/* Main Content */}
      <main className="relative z-0 mt-32 sm:mt-40 max-w-7xl mx-auto px-3 sm:px-6 pb-12">
        {/* Email Sync Status - Show on all tabs */}
        <EmailSyncStatus
          syncStatus={syncStatus}
          newEmails={newEmails}
          isSyncing={isSyncing}
          error={error}
        />

        {activeTab === 'dashboard' && (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 sm:gap-6">
            <div className="lg:col-span-3 space-y-6 sm:space-y-8">
              {/* Welcome Section */}
              <div className="card">
                <h2 className="text-2xl font-bold font-headline mb-3 text-on-surface">
                  Welcome to the Strategy Hub
                </h2>
                <p className="text-on-surface mb-4">
                  This is where the committee plans the track readiness project and develops the
                  club strategy.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <h3 className="text-base sm:text-lg font-bold font-headline mb-2">Two Goals</h3>
                    <ul className="space-y-1 sm:space-y-2 ml-4 text-xs sm:text-sm">
                      <li className="list-disc">
                        <strong>Track Readiness:</strong> Prepare for the new athletics track
                      </li>
                      <li className="list-disc">
                        <strong>Club Strategy:</strong> Plan club growth and how to use the track
                        effectively
                      </li>
                    </ul>
                  </div>
                  <div className="bg-primary-fixed p-3 sm:p-4 rounded-lg flex flex-col justify-center gap-2">
                    <p className="text-xs sm:text-sm text-on-primary-fixed">
                      📖 New to the Strategy Hub? The user guide walks you through every tab.
                    </p>
                    <button
                      onClick={() => setShowGuide(true)}
                      className="btn-primary text-xs sm:text-sm px-3 py-2 self-start"
                    >
                      Open the User Guide
                    </button>
                  </div>
                </div>
              </div>

              {/* Stats Cards */}
              <div>
                <h3 className="text-lg font-bold font-headline mb-4">Overview</h3>
                <StatsCards events={events} files={files} />
              </div>

              {/* Overdue Section */}
              <div>
                <div className="flex items-center mb-4">
                  <h3 className="text-lg font-bold font-headline">⚠️ Overdue Items</h3>
                  {overdue.length > 0 && (
                    <span className="badge bg-error text-on-error ml-2">{overdue.length}</span>
                  )}
                </div>
                <OverdueSection
                  events={overdue}
                  onUpdateEvent={updateEvent}
                  formatDate={formatDate}
                  getTypeColor={getTypeColor}
                />
              </div>

              {/* Upcoming Section */}
              <div>
                <h3 className="text-lg font-bold font-headline mb-4">📅 Upcoming (Next 30 Days)</h3>
                <UpcomingSection
                  events={upcoming}
                  formatDate={formatDate}
                  getTypeColor={getTypeColor}
                  getTypeLabel={getTypeLabel}
                />
              </div>

              {/* Recent Files */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold font-headline">📁 Recent Files</h3>
                  {files.length > 0 && (
                    <button
                      onClick={() => setActiveTab('files')}
                      className="text-primary hover:underline text-sm font-medium"
                    >
                      View all
                    </button>
                  )}
                </div>
                {filesLoading ? (
                  <div className="text-center py-4">Loading files...</div>
                ) : files.length > 0 ? (
                  <div className="space-y-2">
                    {files.slice(0, 5).map((file) => (
                      <div key={file.id} className="card p-3 flex items-center justify-between">
                        <span className="truncate">{file.filename}</span>
                        <span className="text-xs text-on-surface-variant whitespace-nowrap ml-2">
                          {file.category}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="card text-center py-6 text-on-surface-variant">
                    No files yet. Visit the Files tab to upload.
                  </div>
                )}
              </div>
            </div>

            {/* Community Chat Sidebar */}
            <div className="lg:col-span-1">
              <CommunityMessagesBar />
            </div>
          </div>
        )}

        {activeTab === 'tasks' && <TasksTab token={token} />}
        {activeTab === 'files' && <FilesTab token={token} />}
        {activeTab === 'calendar' && <CalendarTab token={token} />}
        {activeTab === 'ideas' && <IdeasTab token={token} />}
        {activeTab === 'club-dev-plan' && <ClubDevelopmentPlanTab email={email} token={token} />}
        {activeTab === 'community' && <CommunityTab token={token} />}
        {activeTab === 'audit' && <AuditTab token={token} />}
        {activeTab === 'admin' && <AdminTab token={token} />}
      </main>

      {showGuide && <UserGuide onClose={() => setShowGuide(false)} />}
    </div>
  );
}
